import { MouseEvent } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import MuiToggleButtonGroup, { ToggleButtonGroupProps } from '@mui/material/ToggleButtonGroup';
import { Box, Typography } from 'ui';
import { styled } from '@mui/material';

const StyledToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButtonGroup-grouped': {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.spacing(0.75),
    marginLeft: 1,
    '&.Mui-disabled': {
      border: 0,
      borderRadius: theme.spacing(0.75)
    },
    '&:not(:first-of-type)': {
      borderRadius: theme.spacing(0.75)
    },
    '&:first-of-type': {
      borderRadius: theme.spacing(0.75),
      marginLeft: theme.spacing(0.5)
    }
  }
}));

const StyledToggleButton = styled(ToggleButton, {
  shouldForwardProp: prop => prop !== 'color'
})<{ color?: Props['color'] }>(({ theme, color = 'primary' }) => ({
  '&.MuiToggleButton-root': {
    border: '0px !important',
    textTransform: 'none'
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette[color].main,
    '&:hover': {
      backgroundColor: theme.palette[color].main
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[500]
    }
  },
  '&.MuiToggleButton-sizeSmall': {
    height: 28
  }
}));

const StyledBorderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: 'fit-content',
  border: `1px solid ${theme.palette.grey[500]}`,
  flexWrap: 'wrap',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius
}));

export type ToggleItem = {
  value: NonNullable<number | string | boolean>;
  label: string;
};

type Props = {
  defaultValue?: any;
  value?: any;
  items: ToggleItem[];
  onToggleChange: (value: any) => void;
  exclusive?: boolean;
  size?: ToggleButtonGroupProps['size'];
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  disabled?: boolean;
  name: string;
};

export default function ToggleButtonGroup({
  items,
  onToggleChange,
  exclusive,
  size = 'small',
  color = 'primary',
  disabled = false,
  value,
  name
}: Props) {
  const handleChange = (event: MouseEvent<HTMLElement>, newValue: any) => {
    if (newValue !== undefined && newValue !== null) {
      onToggleChange(newValue);
    }
  };

  return (
    <StyledBorderBox>
      <StyledToggleButtonGroup
        disabled={disabled}
        size={size}
        color={color}
        value={value}
        exclusive={exclusive}
        onChange={handleChange}
      >
        {items.map(item => (
          <StyledToggleButton name={name} size={size} value={item.value} key={item.label}>
            <Typography variant="body3" color="text.primary">
              {item.label}
            </Typography>
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>
    </StyledBorderBox>
  );
}
